import useGet, { UseGetOptions } from '@/service-library/core-hooks/useGet'
import { getDetail } from '@/service-library/request-wrappers'
import {
  ParamOptionsResponse,
  PositionLookupCatalog,
} from '@/types/position-lookups'

/**
 * MARK: Action Hook: Get Position Lookup Catalog
 * Every question this accounting system can answer about who holds a job, and
 * the blanks a customer fills in to ask one. The answer is the same for every
 * customer on that accounting system, so no company is needed. One that can
 * answer nothing returns an empty `lookups` list rather than an error.
 */
export function useGetPositionLookups({
  orgCredentialTypeId,
  enabled = true,
  ...options
}: UseGetOptions<PositionLookupCatalog> & {
  orgCredentialTypeId: string
  enabled?: boolean
}) {
  const isEnabled = !!orgCredentialTypeId && enabled

  const query = useGet<PositionLookupCatalog>({
    queryKey: ['position-lookups', orgCredentialTypeId],
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return getDetail<PositionLookupCatalog>({
        url: '/v2/relay/positions/lookups',
        ...serviceFnOptions,
        filters: {
          ...filters,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    enabled: isEnabled,
    ...options,
  })

  return {
    positionLookups: query.data?.lookups ?? [],
    ...query,
    // A query that is switched off stays `pending` forever, because nothing
    // will ever fetch and finish it. Callers wait on this before rendering, so
    // passing the raw value on would leave a project with no accounting
    // system spinning instead of being told it has none.
    isPending: isEnabled && query.isPending,
  }
}

/**
 * MARK: Action Hook: Get Position Lookup Param Options
 * The choices for one blank, read from this company's own data. Some blanks
 * can only be filled once another is: which values exist depends on which
 * field was picked. Those go in `dependsOnParams`, and the query waits until
 * every one of them has a value.
 */
export function useGetPositionLookupParamOptions({
  lookupKey,
  paramName,
  orgId,
  orgCredentialTypeId,
  dependsOnParams = {},
  enabled = true,
  ...options
}: UseGetOptions<ParamOptionsResponse> & {
  lookupKey: string
  paramName: string
  orgId: string
  orgCredentialTypeId: string
  dependsOnParams?: Record<string, string | undefined>
  enabled?: boolean
}) {
  const hasEveryDependency = Object.values(dependsOnParams).every(Boolean)

  const query = useGet<ParamOptionsResponse>({
    queryKey: [
      'position-lookup-param-options',
      lookupKey,
      paramName,
      orgId,
      orgCredentialTypeId,
      dependsOnParams,
    ],
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return getDetail<ParamOptionsResponse>({
        url: `/v2/relay/positions/lookups/${encodeURIComponent(
          lookupKey,
        )}/options/${encodeURIComponent(paramName)}`,
        ...serviceFnOptions,
        filters: {
          ...filters,
          ...dependsOnParams,
          org_id: orgId,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    enabled: !!(
      lookupKey &&
      paramName &&
      orgId &&
      orgCredentialTypeId &&
      hasEveryDependency &&
      enabled
    ),
    ...options,
  })

  return {
    paramOptions: query.data?.options ?? [],
    ...query,
  }
}
